import Image from "next/image";
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from "@/components/shadcn/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useState } from "react";

type GalleryImage = {
  url: string;
  alt?: string;
};

type ImageGallerySectionProps = {
  heading?: string;
  images: GalleryImage[];
  columns?: 2 | 3 | 4;
  add_background?: boolean;
  add_bottom_border?: boolean;
};

function GalleryDialogImage({ img, alt }: { img: string; alt: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="w-full h-64 overflow-hidden rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow">
          <Image
            src={img}
            alt={alt}
            width={400}
            height={256}
            className="object-cover w-full h-full"
          />
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-transparent border-0 shadow-none flex items-center justify-center">
        <VisuallyHidden><DialogTitle>{alt}</DialogTitle></VisuallyHidden>
        <Image
          src={img}
          alt={alt}
          width={900}
          height={600}
          className="object-contain w-full max-h-[80vh] rounded-xl"
        />
      </DialogContent>
    </Dialog>
  );
}

export default function ImageGallerySection({
  heading,
  images,
  columns = 3,
  add_background = false,
  add_bottom_border = false,
}: ImageGallerySectionProps) {
  const gridColsClass = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
  }[columns];

  const content = (
    <>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-8">
          {heading}
        </h2>
      )}
      <div
        className={`grid grid-cols-1 ${gridColsClass} gap-6 ${
          add_bottom_border ? "pb-8 border-b border-gray-200" : ""
        }`}
      >
        {images.map((image, idx) => (
          <GalleryDialogImage
            key={idx}
            img={image.url}
            alt={image.alt || `Gallery image ${idx + 1}`}
          />
        ))}
      </div>
    </>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] py-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{
            backgroundImage: "url(/images/left-corner-supergrafis.png)",
          }}
        />
        <div className="container max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {content}
        </div>
      </section>
    );
  }

  return <div className="w-full">{content}</div>;
}
